import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from "react";
import styles from "./SingleFileUpload.module.css";
import FileForm from "./FileForm";
import FilePreview from "./FilePreview";
import ValidationService from "../../Services/validationService";
import FileService from "../../Services/fileService";
import ImageTool from "../../Tools/ImageTools";
import { usePrivileges } from "../../Context/PrivilegeContext";
import { addToast } from "../../Services/toastService";
import i18n from "../../Services/i18n";

const SingleFileUpload = forwardRef(({ onDirtyChange }, ref) => {
  const [file, setFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [formState, setFormState] = useState({
    filename: "",
    tags: "",
    selectedDate: "",
    createDate: null,
    modifyDate: null,
  });

  const { privileges } = usePrivileges();
  const fileInputRef = useRef();

  const notifyDirtyChange = () => {
    onDirtyChange?.();
  };

  useEffect(() => {
    notifyDirtyChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const event = { target: { files: [droppedFile] } };
      onFileChange(event);
    }
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!file) return { success: false };

      const isImage = file.type.startsWith("image/");
      let thumbnail = null;
      let adjustedFile = file;

      if (isImage) {
        thumbnail = await ImageTool.getImageThumbnail(file);
        adjustedFile = await ImageTool.compressToTargetSize(file);
      }

      const date =
        formState.selectedDate === "createDate"
          ? formState.createDate
          : formState.selectedDate === "modifyDate"
            ? formState.modifyDate
            : new Date();

      const result = await FileService.uploadFile(
        adjustedFile,
        thumbnail,
        date,
        formState.filename,
        formState.tags,
      );

      return {
        success: result?.success,
        id: result?.id,
        thumbnail,
      };
    },

    isDirty: () => {
      return !!file;
    },
  }));

  const onFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const _privileges = await privileges();
    const validation = ValidationService.IsSingleFileUploadValid(selectedFile, _privileges);

    if (!validation.isValid) {
      addToast(
        i18n.t(`FileUploaderModal.Validation.${validation.error}`, {
          maxFileSizeMb: Math.round(_privileges.maxFileSize / 1024),
        }),
        "SingleFileValidationError",
        "ERROR",
      );
      return;
    }

    setFile(selectedFile);

    const isImage = selectedFile.type.startsWith("image/");
    if (isImage) {
      const img = new Image();
      img.onload = function () {
        setFormState((prev) => ({
          ...prev,
          createDate: new Date(selectedFile.lastModifiedDate) || null,
          modifyDate: selectedFile.lastModified ? new Date(selectedFile.lastModified) : null,
        }));
      };
      img.src = URL.createObjectURL(selectedFile);
    }

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);

    setFormState((prev) => ({
      ...prev,
      filename: FileService.fileNameWithoutExtension(selectedFile.name),
    }));
  };

  return (
    <div className={styles.container}>
      {!file ? (
        <div
          className={`${styles.dropZone} ${isDragOver ? styles.dragOver : ""}`}
          onClick={() => fileInputRef.current.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}>
          <p>Glissez-déposez un fichier ici ou cliquez pour sélectionner</p>
          <input
            type="file"
            ref={fileInputRef}
            onChange={onFileChange}
            style={{ display: "none" }}
          />
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.preview}>
            <FilePreview file={file} preview={preview} />
          </div>
          <div className={styles.form}>
            <FileForm
              formState={formState}
              onChange={setFormState}
              isImage={file?.type.startsWith("image/")}
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default SingleFileUpload;
